import React from 'react';
import { motion } from 'motion/react';
import { MapPin, Users, Zap, TrendingUp, Clock, CheckCircle, Clock3, XCircle, Building2 } from 'lucide-react';
import { TOrganizationItem } from '@/data/types';

interface OrganizationCardProps {
  organization: TOrganizationItem;
  onSelect: (organizationId: string) => void;
}

const OrganizationCard: React.FC<OrganizationCardProps> = ({ organization, onSelect }) => {
  // Helper functions to derive data from the API response
  const getOrganizationType = (org: TOrganizationItem): string => {
    if (org.name.includes('Δήμος')) return 'Δημοτική Αρχή';
    if (org.name.includes('Περιφέρεια')) return 'Περιφερειακή Αρχή';
    if (org.name.includes('Υπουργείο')) return 'Κυβερνητικός Οργανισμός';
    if (org.name.includes('ΚΕΠ') || org.name.includes('Κέντρο')) return 'Υπηρεσία Πολιτών';
    return 'Οργανισμός';
  };

  const getSubscriptionType = (org: TOrganizationItem): 'enterprise' | 'pro' | 'basic' => {
    if (org.members && org.members > 100) return 'enterprise';
    if (org.members && org.members > 20) return 'pro';
    return 'basic';
  };

  const getOrganizationStatus = (org: TOrganizationItem): 'active' | 'pending' | 'inactive' => {
    if (org.hidden) return 'inactive';
    // You can add more logic here based on other fields
    return 'active';
  };

  const getLastActiveTime = (org: TOrganizationItem): string => {
    const updatedDate = new Date(org.updated_at);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - updatedDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Σήμερα';
    if (diffDays === 2) return 'Χθες';
    if (diffDays <= 7) return `${diffDays} ημέρες πριν`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} εβδομάδες πριν`;
    return `${Math.ceil(diffDays / 30)} μήνες πριν`;
  };

  const getLocationDisplay = (org: TOrganizationItem): string => {
    if (org.location && typeof org.location === 'object' && org.location.name) {
      return org.location.name;
    }
    if (org.location_id) return 'Ελλάδα';
    return 'Άγνωστη τοποθεσία';
  };

  const getLogo = (org: TOrganizationItem): string => {
    if (org.image_url) return org.image_url;
    return org.name.charAt(0).toUpperCase();
  };

  const getEnergySystemsCount = (org: TOrganizationItem): number => {
    // Mock data for now - you can replace this with actual logic
    // based on your API data structure
    return Math.floor(Math.random() * 50) + 1;
  };

  const getTotalEnergyGenerated = (org: TOrganizationItem): string => {
    // Mock data for now - you can replace this with actual logic
    return `${Math.floor(Math.random() * 1000) + 100} kWh`;
  };

  // Derived values
  const status = getOrganizationStatus(organization);
  const subscription = getSubscriptionType(organization);
  const industry = getOrganizationType(organization);
  const location = getLocationDisplay(organization);
  const logo = getLogo(organization);
  const memberCount = organization.members || 0;
  const energySystems = getEnergySystemsCount(organization);
  const totalEnergyGenerated = getTotalEnergyGenerated(organization);
  const lastActive = getLastActiveTime(organization);
  const description = organization.description || 'Δεν υπάρχει περιγραφή';

  const getStatusIcon = (status: 'active' | 'pending' | 'inactive') => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-3 w-3 text-emerald-500" />;
      case 'pending':
        return <Clock3 className="h-3 w-3 text-amber-500" />;
      case 'inactive':
        return <XCircle className="h-3 w-3 text-gray-400" />;
      default:
        return <XCircle className="h-3 w-3 text-gray-400" />;
    }
  };

  const getStatusText = (status: 'active' | 'pending' | 'inactive') => {
    switch (status) {
      case 'active':
        return 'Ενεργός';
      case 'pending':
        return 'Εκκρεμής';
      case 'inactive':
        return 'Ανενεργός';
      default:
        return 'Άγνωστη';
    }
  };

  const getSubscriptionGradient = (subscription: 'enterprise' | 'pro' | 'basic') => {
    switch (subscription) {
      case 'enterprise':
        return 'from-purple-500 to-purple-600';
      case 'pro':
        return 'from-blue-500 to-blue-600';
      case 'basic':
        return 'from-gray-400 to-gray-500';
      default:
        return 'from-gray-400 to-gray-500';
    }
  };

  const getSubscriptionText = (subscription: 'enterprise' | 'pro' | 'basic') => {
    switch (subscription) {
      case 'enterprise':
        return 'Enterprise';
      case 'pro':
        return 'Pro';
      case 'basic':
        return 'Basic';
      default:
        return 'Basic';
    }
  };

  const renderLogo = () => {
    if (organization.image_url) {
      return (
        <img
          src={organization.image_url}
          alt={organization.name}
          className="h-14 w-14 rounded-2xl object-cover shadow-sm"
        />
      );
    }

    return (
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 text-2xl font-semibold text-gray-700 shadow-sm">
        {logo}
      </div>
    );
  };

  return (
    <motion.div
      whileHover={{ y: -6 }}
      whileTap={{ scale: 0.98 }}
      className="group relative cursor-pointer overflow-hidden rounded-3xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-500 hover:border-gray-200 hover:shadow-xl hover:shadow-blue-500/5"
      onClick={() => onSelect(organization.id)}
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 h-32 w-32 rounded-bl-full bg-gradient-to-br from-gray-50/50 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="absolute -top-4 -right-4 h-20 w-20 rounded-full bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-0 transition-all duration-700 group-hover:scale-150 group-hover:opacity-100" />

      {/* Top Section */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <div className="relative">
            {renderLogo()}
            {/* Status Indicator */}
            <div className="absolute -right-1 -bottom-1 rounded-full bg-white p-1 shadow-sm">
              {getStatusIcon(status)}
            </div>
          </div>

          {/* Name & Location */}
          <div className="space-y-1">
            <h3 className="font-semibold text-gray-900 transition-colors duration-300 group-hover:text-gray-700">
              {organization.name}
            </h3>
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <MapPin className="h-3.5 w-3.5" />
              <span>{location}</span>
            </div>
          </div>
        </div>

        {/* Subscription Badge */}
        <div className="relative">
          <div className="relative">
            <div
              className={`h-2 w-16 rounded-full bg-gradient-to-r ${getSubscriptionGradient(subscription)} shadow-sm`}
            />
            <div className="absolute inset-0 rounded-full bg-white/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          </div>
          <span className="mt-1 block text-xs font-medium text-gray-600">{getSubscriptionText(subscription)}</span>
          {/* Decorative dot */}
          <div className="absolute -top-1 -right-2 h-1 w-1 rounded-full bg-gray-300 transition-all duration-500 group-hover:scale-150 group-hover:bg-blue-400" />
        </div>
      </div>

      {/* Description */}
      <p className="mb-6 line-clamp-2 text-sm leading-relaxed text-gray-600">{description}</p>

      {/* Stats Grid */}
      <div className="relative mb-6">
        {/* Background decoration for stats */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-gray-50/50 via-transparent to-gray-50/50 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

        <div className="relative grid grid-cols-3 gap-4 p-4">
          <div className="text-center">
            <div className="mb-2 flex items-center justify-center">
              <div className="rounded-full bg-gray-100 p-2 transition-all duration-300 group-hover:bg-blue-50">
                <Users className="h-4 w-4 text-gray-400 transition-colors duration-300 group-hover:text-blue-500" />
              </div>
            </div>
            <p className="text-lg font-semibold text-gray-900">{memberCount}</p>
            <p className="text-xs text-gray-500">Μέλη</p>
          </div>

          <div className="relative text-center">
            <div className="mb-2 flex items-center justify-center">
              <div className="rounded-full bg-gray-100 p-2 transition-all duration-300 group-hover:bg-amber-50">
                <Zap className="h-4 w-4 text-gray-400 transition-colors duration-300 group-hover:text-amber-500" />
              </div>
            </div>
            <p className="text-lg font-semibold text-gray-900">{energySystems}</p>
            <p className="text-xs text-gray-500">Συστήματα</p>
            {/* Center divider */}
            <div className="absolute top-1/2 left-0 h-8 w-px -translate-y-1/2 bg-gray-200" />
            <div className="absolute top-1/2 right-0 h-8 w-px -translate-y-1/2 bg-gray-200" />
          </div>

          <div className="text-center">
            <div className="mb-2 flex items-center justify-center">
              <div className="rounded-full bg-emerald-50 p-2 transition-all duration-300 group-hover:bg-emerald-100">
                <TrendingUp className="h-4 w-4 text-emerald-500 transition-transform duration-300 group-hover:scale-110" />
              </div>
            </div>
            <p className="text-lg font-semibold text-emerald-600">{totalEnergyGenerated}</p>
            <p className="text-xs text-gray-500">Ενέργεια</p>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="relative flex items-center justify-between border-t border-gray-50 pt-4">
        {/* Industry with icon */}
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-blue-400 opacity-60" />
          <span className="rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 transition-colors duration-300 group-hover:bg-blue-50 group-hover:text-blue-700">
            {industry}
          </span>
        </div>

        {/* Last Active with enhanced styling */}
        <div className="flex items-center gap-1.5 rounded-full bg-gray-50 px-3 py-1 text-xs text-gray-500 transition-all duration-300 group-hover:bg-gray-100">
          <Clock className="h-3 w-3 transition-transform duration-300 group-hover:rotate-12" />
          <span>{lastActive}</span>
        </div>

        {/* Decorative corner element */}
        <div className="absolute -right-2 -bottom-2 h-4 w-4 rounded-tl-full border-t border-l border-gray-100 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      </div>

      {/* Contact Information Tooltip (if available) */}
      {(organization.contact_email || organization.website_url) && (
        <div className="absolute top-16 right-4 opacity-0 transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
          <div className="rounded-lg bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm">
            {organization.contact_email && (
              <p className="mb-1 text-xs text-gray-600">📧 {organization.contact_email}</p>
            )}
            {organization.website_url && <p className="text-xs text-blue-600">🌐 Website</p>}
          </div>
        </div>
      )}

      {/* Enhanced Hover Gradient Overlay */}
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/0 via-transparent to-purple-500/0 opacity-0 transition-opacity duration-500 group-hover:from-blue-500/[0.03] group-hover:to-purple-500/[0.02] group-hover:opacity-100" />

      {/* Subtle animated border */}
      <div
        className="absolute inset-0 rounded-3xl border border-transparent bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          maskImage: 'linear-gradient(transparent, transparent)',
          WebkitMaskImage: 'linear-gradient(transparent, transparent)',
        }}
      />

      {/* Status Text Tooltip */}
      <div className="absolute top-4 right-4 opacity-0 transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100">
        <div className="rounded-lg bg-white/90 px-2 py-1 shadow-sm backdrop-blur-sm">
          <span className="text-xs font-medium text-gray-600">{getStatusText(status)}</span>
        </div>
      </div>

      {/* Floating action indicator */}
      <div className="absolute right-6 bottom-6 translate-x-2 opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 shadow-lg shadow-blue-500/25">
          <div className="h-2 w-2 animate-pulse rounded-full bg-white" />
        </div>
      </div>
    </motion.div>
  );
};

export default OrganizationCard;
